import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, RefreshCcw } from 'lucide-react';
interface SlideProps {
  index: number;
  total: number;
}
export function Slide4Opportunity({
  index
}: SlideProps) {
  const comparisons = [{
    passive: 'HCP realizes a problem',
    active: 'Snak presents a relevant problem'
  }, {
    passive: 'HCP searches for a solution',
    active: 'Snak creates curiosity and relevance'
  }, {
    passive: 'HCP finds LEO / Anzupgo',
    active: 'Snak introduces the relevant Anzupgo message'
  }, {
    passive: 'HCP navigates the website',
    active: 'Snak guides them to the right content'
  }, {
    passive: 'Website handles objections',
    active: 'Snak captures barriers and responds'
  }, {
    passive: 'Engagement ends on the site',
    active: 'Snak becomes reusable outbound content'
  }];
  return <div className="relative w-screen h-screen overflow-hidden bg-[var(--bg)] p-[clamp(2rem,4vw,4rem)] flex flex-col" data-id="element-180">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.2,
      duration: 0.6
    }} className="mb-[clamp(1.5rem,2.5vw,2.5rem)] flex-shrink-0 flex justify-between items-end" data-id="element-181">
        <div data-id="element-182">
          <p className="text-[var(--brand-green)] text-[clamp(0.875rem,1.5vw,1.125rem)] font-semibold uppercase tracking-wider mb-[clamp(0.5rem,1vw,1rem)]" data-id="element-183">
            The Opportunity
          </p>
          <h2 className="text-[var(--fg)] font-bold text-[clamp(2rem,3.6vw,3.25rem)] leading-[1.08]" data-id="element-184">
            Reversing the{' '}
            <span className="text-[var(--brand-green-light)]" data-id="element-185">HCP Journey</span>
          </h2>
        </div>
        <div className="hidden md:block w-48" data-id="element-186">
          <img src="https://cdn.magicpatterns.com/uploads/oqxxsE4jBTCWxtbqM2Gzsm/Screenshot_2026-06-29_at_10.13.11.png" alt="Anzupgo Logo" className="w-full h-auto object-contain" data-id="element-187" />
        </div>
      </motion.div>

      <div className="flex-1 grid grid-cols-12 gap-[clamp(1.5rem,3vw,3rem)] min-h-0" data-id="element-188">
        <motion.div initial={{
        opacity: 0,
        x: -20
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.4,
        duration: 0.6
      }} className="col-span-5 rounded-3xl overflow-hidden shadow-lg relative" data-id="element-189">
          <motion.video src="https://cdn.magicpatterns.com/uploads/25S5nZRWwNnYGPLCXbcRti/eye.mp4" autoPlay loop muted playsInline className="w-full h-full object-cover" data-id="element-190" />
          <div className="absolute inset-0 bg-[var(--brand-green)]/20 mix-blend-multiply" data-id="element-191" />

          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10" data-id="element-192">
            <motion.span initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 1,
            duration: 0.8,
            ease: 'easeOut'
          }} className="text-white font-bold text-[clamp(2rem,4vw,3.5rem)] uppercase tracking-[0.25em] drop-shadow-xl" data-id="element-193">
              Engage
            </motion.span>
          </div>
        </motion.div>

        <div className="col-span-7 flex flex-col justify-center min-h-0" data-id="element-194">
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.6,
          duration: 0.6
        }} className="w-full bg-white rounded-2xl shadow-sm border border-black/5 overflow-hidden" data-id="element-195">
            <div className="grid grid-cols-2 bg-[var(--surface)] border-b border-black/5" data-id="element-196">
              <div className="p-5 text-center" data-id="element-197">
                <h3 className="text-[var(--muted)] font-bold text-sm uppercase tracking-wider" data-id="element-198">
                  Traditional Website Journey
                </h3>
              </div>
              <div className="p-5 text-center bg-[var(--brand-green)]/5 flex items-center justify-center gap-2" data-id="element-199">
                <RefreshCcw className="w-4 h-4 text-[var(--brand-green)]" data-id="element-200" />
                <h3 className="text-[var(--brand-green)] font-bold text-sm uppercase tracking-wider" data-id="element-201">
                  Snakker-Activated Journey
                </h3>
              </div>
            </div>

            <div className="divide-y divide-black/5" data-id="element-202">
              {comparisons.map((item, idx) => <motion.div key={idx} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.7 + idx * 0.1,
              duration: 0.4
            }} className="grid grid-cols-2 relative" data-id="element-203">
                  <div className="p-3 px-6 flex items-center justify-end text-right" data-id="element-204">
                    <p className="text-[var(--muted)] font-medium text-[clamp(0.8rem,0.9vw,1rem)]" data-id="element-205">
                      {item.passive}
                    </p>
                  </div>
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-6 h-6 bg-white rounded-full border border-black/5 flex items-center justify-center shadow-sm" data-id="element-206">
                    <ArrowRight className="w-3 h-3 text-[var(--brand-green)]" data-id="element-207" />
                  </div>
                  <div className="p-3 px-6 flex items-center bg-[var(--brand-green)]/5" data-id="element-208">
                    <p className="text-[var(--fg)] font-bold text-[clamp(0.8rem,0.9vw,1rem)]" data-id="element-209">
                      {item.active}
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>

          <motion.p initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 1.4,
          duration: 0.6
        }} className="mt-6 text-[var(--muted)] font-medium text-[clamp(0.9rem,1vw,1.1rem)] leading-relaxed" data-id="element-210">
            Instead of waiting for HCPs to find the relevant content, Snakker
            creates short, provocative, problem-led entry points that bring the
            relevant Anzupgo message to the HCP first.
          </motion.p>
        </div>
      </div>
    </div>;
}